import React, { useState } from 'react';
import ImageUploader from '../components/ImageUploader';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { buildApiUrl } from '../config/api';
import { parseMakeupApiResponse } from '../utils/makeupApi';

export default function Dashboard() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('processor');
  const [recentImages, setRecentImages] = useState([]);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [showResults, setShowResults] = useState(false);
  // const [error, setError] = useState(''); // eslint-disable-line no-unused-vars
  
  // Beispieldaten für den Benutzer
  const user = {
    name: "Max Mustermann",
    email: "max@example.com",
    plan: "Premium",
    imagesUsed: 23,
    totalImages: 100,
    nextRenewal: "24.05.2023"
  };

  // Funktion zum Verarbeiten eines Bildes
  const processImage = async (file) => {
    setIsLoading(true);
    
    // Speichere das originale Bild
    const reader = new FileReader();
    reader.onload = async () => {
      setBeforeImage(reader.result);
      
      try {
        // API-Aufruf an den Replicate-Proxy
        const formData = new FormData();
        formData.append('image', file);

        const response = await fetch(buildApiUrl('/api/remove-makeup'), {
          method: 'POST',
          body: formData,
        });

        const result = await parseMakeupApiResponse(response);

        if (result.type === 'image' || result.type === 'image-url') {
          setAfterImage(result.url);
          setShowResults(true);
          setIsLoading(false);

          const now = new Date();
          setRecentImages(prev => [
            {
              id: Date.now(),
              before: reader.result,
              after: result.url,
              date: now.toLocaleDateString('de-DE'),
              time: now.toLocaleTimeString('de-DE')
            },
            ...prev.slice(0, 9)
          ]);
        } else {
          const checkInterval = setInterval(async () => {
            const resultResponse = await fetch(result.url);
            const resultData = await resultResponse.json();

            if (resultData.status === 'succeeded' && resultData.output) {
              clearInterval(checkInterval);
              setAfterImage(resultData.output[0]); // Replicate gibt ein Array zurück
              setShowResults(true);
              setIsLoading(false);
              
              // Füge das Bild zur Verlaufsliste hinzu
              const now = new Date();
              setRecentImages(prev => [
                {
                  id: Date.now(),
                  before: reader.result,
                  after: resultData.output[0],
                  date: now.toLocaleDateString('de-DE'),
                  time: now.toLocaleTimeString('de-DE')
                },
                ...prev.slice(0, 9) // Behalte maximal 10 Bilder
              ]);
            } else if (resultData.status === 'failed') {
              clearInterval(checkInterval);
              // setError('KI-Verarbeitung fehlgeschlagen. Bitte versuche es erneut.');
              setIsLoading(false);
            }
          }, 3000); // Überprüfe alle 3 Sekunden
        }
      } catch (e) {
        // setError(`Fehler beim Senden der Anfrage: ${e.message}`);
        setIsLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <h1 className="text-2xl font-bold text-primary-600">Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                {user.plan}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Benutzerinfo Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-6">
            <div className="px-4 py-5 sm:p-6">
              <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Willkommen zurück, {user.name}!
                  </h3>
                  <div className="mt-2 max-w-xl text-sm text-gray-500">
                    <p>{user.email}</p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-0">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-3">
                      {user.imagesUsed} von {user.totalImages} Bildern verwendet
                    </span>
                    <div className="w-48 bg-gray-200 rounded-full h-2.5">
                      <div 
                        className="bg-primary-600 h-2.5 rounded-full" 
                        style={{ width: `${(user.imagesUsed / user.totalImages) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Nächste Erneuerung am {user.nextRenewal}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="sm:hidden">
              <label htmlFor="tabs" className="sr-only">
                Tab wählen
              </label>
              <select
                id="tabs"
                name="tabs"
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
              >
                <option value="processor">Bild verarbeiten</option>
                <option value="history">Verlauf</option>
                <option value="settings">Einstellungen</option>
              </select>
            </div>
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('processor')}
                    className={`${
                      activeTab === 'processor'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Bild verarbeiten
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`${
                      activeTab === 'history'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Verlauf
                  </button>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`${
                      activeTab === 'settings'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Einstellungen
                  </button>
                </nav>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {/* Bild verarbeiten Tab */}
            {activeTab === 'processor' && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Makeup entfernen
                  </h3>
                  <div className="mt-2 max-w-xl text-sm text-gray-500">
                    <p>
                      Lade ein Bild mit geschminktem Gesicht hoch und unsere KI entfernt das Makeup.
                    </p>
                  </div>
                  
                  <div className="mt-5">
                    {!showResults ? (
                      <>
                        <ImageUploader onImageUpload={processImage} />
                        
                        {isLoading && (
                          <div className="mt-6 text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                            <p className="mt-2 text-primary-600">Verarbeite Bild...</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <BeforeAfterSlider 
                          beforeImage={beforeImage}
                          afterImage={afterImage}
                        />
                        
                        <div className="mt-6">
                          <button
                            onClick={() => setShowResults(false)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Neues Bild hochladen
                          </button>
                          <button
                            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            Bild herunterladen
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Verlauf Tab */}
            {activeTab === 'history' && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Deine letzten Bilder
                  </h3>
                  
                  {recentImages.length > 0 ? (
                    <div className="mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
                      {recentImages.map((image) => (
                        <div key={image.id} className="group">
                          <div className="w-full bg-gray-200 rounded-lg overflow-hidden aspect-w-3 aspect-h-2">
                            <BeforeAfterSlider 
                              beforeImage={image.before}
                              afterImage={image.after}
                            />
                          </div>
                          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                            <p>{image.date}, {image.time}</p>
                            <button className="text-primary-600 hover:text-primary-900">
                              Herunterladen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Bilder vorhanden</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Du hast noch keine Bilder verarbeitet.
                      </p>
                      <div className="mt-6">
                        <button
                          onClick={() => setActiveTab('processor')}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Zum Bild-Prozessor
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Einstellungen Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Kontoeinstellungen
                  </h3>
                  
                  <div className="mt-6 divide-y divide-gray-200">
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Name</dt>
                      <dd className="mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span>{user.name}</span>
                        <button className="text-primary-600 hover:text-primary-900">
                          Ändern
                        </button>
                      </dd>
                    </div>
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">E-Mail</dt>
                      <dd className="mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span>{user.email}</span>
                        <button className="text-primary-600 hover:text-primary-900">
                          Ändern
                        </button>
                      </dd>
                    </div>
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Passwort</dt>
                      <dd className="mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span>••••••••</span>
                        <button className="text-primary-600 hover:text-primary-900">
                          Ändern
                        </button>
                      </dd>
                    </div>
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Aktueller Plan</dt>
                      <dd className="mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span>{user.plan} (bis {user.nextRenewal})</span>
                        <button className="text-primary-600 hover:text-primary-900">
                          Upgrade
                        </button>
                      </dd>
                    </div>
                    <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                      <dt className="text-sm font-medium text-gray-500">Zahlungsmethode</dt>
                      <dd className="mt-1 flex justify-between text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span>Visa **** 4242</span>
                        <button className="text-primary-600 hover:text-primary-900">
                          Ändern
                        </button>
                      </dd>
                    </div>
                  </div>
                  
                  <div className="mt-8 border-t border-gray-200 pt-6">
                    <h4 className="text-md font-medium text-gray-900">Datenschutz</h4>
                    
                    <div className="mt-4">
                      <div className="flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="newsletter"
                            name="newsletter"
                            type="checkbox"
                            defaultChecked
                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="newsletter" className="font-medium text-gray-700">
                            Newsletter
                          </label>
                          <p className="text-gray-500">
                            E-Mails mit Neuigkeiten und Tipps zu NaturalMe erhalten.
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id="data-retention"
                            name="data-retention"
                            type="checkbox"
                            defaultChecked
                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="data-retention" className="font-medium text-gray-700">
                            Bilderverlauf
                          </label>
                          <p className="text-gray-500">
                            Verarbeitete Bilder für 30 Tage speichern.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex justify-between">
                    <button
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Konto löschen
                    </button>
                    <button
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Änderungen speichern
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
